import { action, flow, makeObservable } from 'mobx';
import { PostClosingTabsEnum } from '../constants/tabs';
import { PostClosingBaseStore } from './postClosingBaseStore';
import { GlobalStore } from '@roc/feature-app-core';
import { PostClosingModalStore } from './postClosingModalStore';

export class PostClosingDashboardStore extends PostClosingBaseStore {
  modalStore = new PostClosingModalStore();
  constructor(globalStore: GlobalStore) {
    super(globalStore);
    this.tab = PostClosingTabsEnum.TRAILING_DOCUMENTS;

    makeObservable(this, {
      fetchDropdowns: flow,
      exportDashboard: flow,
      updateCollateralFieldForLoans: flow,
      openLoanActionModal: action,
      openLoanDateModal: action,
      openLoanNumberModal: action,
      openLoanSpecialistModal: flow,
    });
  }

  *fetchDropdowns() {
    try {
      const specialists = yield this.service.getAllPostClosingSpecialists();
      return specialists.data;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error fetching specialist dropdowns.',
      });
    }
  }

  *exportDashboard() {
    try {
      const filtersObj = this.postClosingGridStore.gridData.meta.filters;
      const filters =
        filtersObj && Object.keys(filtersObj).length > 0
          ? JSON.stringify(filtersObj)
          : ' ';

      let loanProcess = '';
      switch (this.tab) {
        case PostClosingTabsEnum.TRAILING_DOCUMENTS:
          loanProcess = 'Post Closing Trailing Documents';
          break;
        case PostClosingTabsEnum.COLLATERAL_MANAGEMENT:
          loanProcess = 'Post Closing Collateral Management';
          break;
        default:
          loanProcess = '';
      }

      const { data, headers } = yield this.service.exportDashboard(
        loanProcess,
        filters
      );

      const blob = new Blob([data], {
        type:
          headers?.['content-type'] ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const filename =
        this.tab === PostClosingTabsEnum.COLLATERAL_MANAGEMENT
          ? 'Collateral Management Dashboard.xlsx'
          : 'Trailing Documents Dashboard.xlsx';

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error exporting dashboard.',
      });
    }
  }

  reset() {
    this.postClosingGridStore.reset();
    this.clearSelectedLoanIds();
  }

  openLoanActionModal = ({ title, fieldKey, loans }) => {
    this.modalStore.open({
      type: 'boolean',
      title,
      fieldKey,
      loans,
    });
  };

  openLoanDateModal = ({ title, fieldKey, loans }) => {
    this.modalStore.open({
      type: 'date',
      title,
      fieldKey,
      loans,
    });
  };

  openLoanNumberModal = ({ title, fieldKey, loans }) => {
    this.modalStore.open({
      type: 'number',
      title,
      fieldKey,
      loans,
      options: { onlyInteger: true },
    });
  };

  updateCollateralFieldForLoans = flow(function* (
    this: PostClosingDashboardStore,
    fieldKey: string,
    loans: any[],
    value: any,
    gridRef?: any
  ) {
    try {
      const updates = loans.map(loan => ({
        loanId: loan,
        [fieldKey]: value,
      }));

      yield this.service.updateCollateralFields(updates);

      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Field updated successfully.',
      });

      this.modalStore.close();
      this.clearSelectedLoanIds();

      gridRef?.current?.deselectAll?.();
      gridRef?.current?.refresh?.();

      yield this.resetAndFetchGridData();
    } catch (error) {
      console.error('Update error:', error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Failed to update field.',
      });
    }
  });

  openLoanSpecialistModal = flow(function* (
    this: PostClosingDashboardStore,
    { title, loans }
  ) {
    try {
      const emails: string[] = yield this.service.getAllPostClosingSpecialists();
      this.modalStore.open({
        type: 'specialist',
        title,
        loans,
        specialists: emails,
      });
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Failed to load specialists.',
      });
    }
  });

}
